// === PageSearch — "Go to page…" search in the top bar (same as ICU Admin) ===
// Type a page name, pick it, go there. Ctrl+K / ⌘K focuses it from anywhere.
// Options are the dashboard cards this user's job title allows
// (useVisibleNavGroups), so the search never offers a page the dashboard hides.

import { cloneElement, useEffect, useMemo, useRef } from 'react';
import type { HTMLAttributes, JSX, ReactNode } from 'react';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import { Search as SearchIcon, Dashboard as DashboardIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVisibleNavGroups } from '../lib/card-access-context';

interface PageOption {
  readonly label: string;
  readonly group: string;
  readonly path: string;
  readonly icon: JSX.Element;
  readonly color: string;
}

interface PageSearchProps {
  /** Dark top bar (gradient themes) vs the white theme. */
  readonly onDark: boolean;
  readonly autoFocus?: boolean;
  /** Called after a page is picked or the field loses focus (phone: closes the search). */
  readonly onDone?: () => void;
}

export function PageSearch({ onDark, autoFocus = false, onDone }: PageSearchProps): JSX.Element {
  const navigate = useNavigate();
  const groups = useVisibleNavGroups();
  const rootRef = useRef<HTMLDivElement>(null);

  const options = useMemo<readonly PageOption[]>(() => [
    { label: 'Dashboard', group: 'Home', path: '/dashboard', icon: <DashboardIcon />, color: '#2e7d32' },
    ...groups.flatMap((g) => g.items
      .filter((item) => item.available)
      .map((item) => ({ label: item.label, group: g.heading, path: item.path, icon: item.icon, color: item.color }))),
  ], [groups]);

  // Ctrl+K / ⌘K focuses the search from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        rootRef.current?.querySelector('input')?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const fg = onDark ? '#ffffff' : undefined;

  return (
    <Autocomplete<PageOption, false, false, false>
      ref={rootRef}
      options={options}
      groupBy={(o) => o.group}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.path === b.path}
      value={null}
      blurOnSelect
      autoHighlight
      openOnFocus={autoFocus}
      onChange={(_e, o) => {
        if (o) void navigate(o.path);
        onDone?.();
      }}
      onBlur={() => onDone?.()}
      renderOption={(props, o) => {
        const { key, ...rest } = props as HTMLAttributes<HTMLLIElement> & { key: string };
        return (
          <li key={key} {...rest}>
            {cloneElement(o.icon, { fontSize: 'small', sx: { color: o.color, mr: 1.5 } })}
            {o.label}
          </li>
        );
      }}
      renderInput={(params) => {
        // Autocomplete's params carry optional props that exactOptionalPropertyTypes
        // rejects when spread straight into TextField (same cast as ContractorEditDialog).
        const p = params as unknown as TextFieldProps & { InputProps: Record<string, unknown> & { endAdornment?: ReactNode } };
        return (
        <TextField
          {...p}
          autoFocus={autoFocus}
          size="small"
          placeholder="Go to page…  (Ctrl+K)"
          slotProps={{
            input: {
              ...p.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: onDark ? 'rgba(255,255,255,0.85)' : 'text.secondary' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: onDark ? 'rgba(255,255,255,0.08)' : 'action.hover',
              borderRadius: 2,
              color: fg,
              '& fieldset': { borderColor: onDark ? 'rgba(255,255,255,0.3)' : undefined },
              '&:hover fieldset': { borderColor: onDark ? 'rgba(255,255,255,0.6)' : undefined },
            },
            '& input::placeholder': { color: onDark ? 'rgba(255,255,255,0.75)' : undefined, opacity: 1 },
            '& .MuiAutocomplete-popupIndicator': { color: fg },
          }}
        />
        );
      }}
      sx={{ width: '100%' }}
    />
  );
}
